import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { listPayroll, listEmployees } from '../lib/api';
import { formatRp } from '../lib/format';

export default function PayrollSlipPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [emp, setEmp] = useState(null);

  useEffect(() => {
    (async () => {
      const [runs, emps] = await Promise.all([listPayroll(), listEmployees()]);
      const r = runs.find((x) => x.id === runId);
      setRun(r || null);
      if (r) setEmp(emps.find((e) => e.id === r.employee_id) || null);
    })();
  }, [runId]);

  if (!run) {
    return (
      <div className="min-h-screen bg-stone-50 p-8">
        <div className="max-w-3xl mx-auto text-center text-stone-500 mt-24">
          Memuat slip…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 print:py-0 print:bg-white">
      <div className="max-w-3xl mx-auto px-6 no-print flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/payroll')} data-testid="slip-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
        </Button>
        <Button onClick={() => window.print()}
          className="bg-emerald-800 hover:bg-emerald-900 text-white"
          data-testid="slip-print">
          <Printer className="w-4 h-4 mr-1.5" /> Cetak / PDF
        </Button>
      </div>

      <div className="max-w-3xl mx-auto bg-white border border-stone-200 rounded-lg p-10 print:border-0 print:rounded-none">
        <div className="text-center mb-6 pb-6 border-b border-stone-200">
          <h1 className="font-heading text-2xl font-bold text-stone-900">SLIP GAJI KARYAWAN</h1>
          <p className="text-sm text-stone-600 mt-1">Toko Main Bulutangkis · MAINBULUTANGKIS ERP</p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Nama Karyawan</div>
            <div className="font-semibold text-stone-900">{run.employee_name}</div>
            {emp?.position && <div className="text-sm text-stone-600">{emp.position}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Periode</div>
            <div className="font-semibold text-stone-900">{run.period || '—'}</div>
            <div className="text-sm text-stone-600">Tanggal Bayar: {run.date}</div>
          </div>
          {emp?.bank_name && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Bank / No. Rekening</div>
              <div className="font-medium text-stone-800">{emp.bank_name}</div>
              <div className="font-mono text-sm text-stone-600">{emp.bank_account || '—'}</div>
            </div>
          )}
          <div className={emp?.bank_name ? 'text-right' : 'col-span-2 text-right'}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Ref Jurnal</div>
            <div className="font-mono text-sm text-emerald-900">{run.journal_ref || '—'}</div>
          </div>
        </div>

        <table className="w-full mb-6">
          <thead>
            <tr className="border-b-2 border-stone-300">
              <th className="text-left py-2 text-[11px] uppercase tracking-widest text-stone-600">Komponen</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-widest text-stone-600">Nilai</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-stone-100">
              <td className="py-2.5 text-stone-800">Gaji Pokok</td>
              <td className="py-2.5 text-right num font-medium">{formatRp(run.gaji_pokok || 0)}</td>
            </tr>
            {run.dasar_komisi > 0 && (
              <tr className="border-b border-stone-100">
                <td className="py-2.5">
                  <div className="text-stone-800">Komisi</div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {formatRp(run.dasar_komisi)} × {run.rate_komisi}%
                  </div>
                </td>
                <td className="py-2.5 text-right num font-medium text-emerald-800">
                  + {formatRp(run.komisi || 0)}
                </td>
              </tr>
            )}
            {run.potongan > 0 && (
              <tr className="border-b border-stone-100">
                <td className="py-2.5 text-stone-800">Potongan (Absen / Lainnya)</td>
                <td className="py-2.5 text-right num font-medium text-red-700">
                  − {formatRp(run.potongan)}
                </td>
              </tr>
            )}
            {run.description && (
              <tr className="border-b border-stone-100">
                <td className="py-2.5 text-stone-800 text-sm italic">{run.description}</td>
                <td></td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-stone-400">
              <td className="py-3 font-bold text-stone-900 text-lg">Total Diterima</td>
              <td className="py-3 text-right num font-bold text-emerald-900 text-lg">
                {formatRp(run.amount)}
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="grid grid-cols-2 gap-8 mt-16 text-sm">
          <div className="text-center">
            <div className="mb-16 text-stone-600">Dibuat oleh,</div>
            <div className="border-t border-stone-400 pt-1">
              ( {run.created_by || 'Admin'} )
            </div>
          </div>
          <div className="text-center">
            <div className="mb-16 text-stone-600">Diterima oleh,</div>
            <div className="border-t border-stone-400 pt-1">
              ( {run.employee_name} )
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
